import {
  Box,
  Checkbox,
  Field,
  Flex,
  Heading,
  Text,
  Input,
  InputMask,
  Select,
  Button,
  MaskEnum,
} from 'ui'
import { Controller, FieldErrors, UseFormRegister } from 'react-hook-form'
import { Selectable } from 'typings/selectable_ext'
import { ShiftSupervisor, StaffForm } from '../../schemas'
import { useCompanyRoles } from 'queries/company'
import { useMemo } from 'react'

export type RoleOption = {
  value: number
  label: string
}

type ExternalSupervisorProps = {
  index: number
  errors?: FieldErrors<ShiftSupervisor>
  register: UseFormRegister<StaffForm>
  sendInvite: boolean
  onRemove: () => void
}

export function ExternalSupervisor({
  index,
  errors,
  register,
  sendInvite,
  onRemove,
}: ExternalSupervisorProps) {
  const { data: roles } = useCompanyRoles()
  const roleOptions = useMemo(() => {
    if (!roles) return []

    return roles.map((role) => ({
      value: role.id,
      label: role.description ?? '',
    }))
  }, [roles])

  return (
    <Box width={1}>
      <Flex>
        <Heading level={4} my={3} mt={2} color={'primary'}>
          Assign a Supervisor
        </Heading>
        <Box flex={1} />
        <Button onClick={onRemove} variant={'text'}>
          Cancel
        </Button>
      </Flex>
      <Flex flexWrap={'wrap'}>
        <Box width={[1, 1 / 2]} pr={[0, 2]}>
          <Field
            label={'Enter Assignee Name'}
            error={errors?.name?.message?.toString()}
          >
            <Input
              {...register(`supervisors.${index}.name` as const)}
              placeholder={'First name, Last name'}
            />
          </Field>
        </Box>
        <Box width={[1, 1 / 2]} pr={[0, 2]}>
          <Field
            label={'Phone Number*'}
            error={errors?.phoneNumber?.message?.toString()}
          >
            <Controller
              name={`supervisors.${index}.phoneNumber` as const}
              render={({ field }) => (
                <InputMask mask={MaskEnum.PhoneNumberAlternate} {...field} />
              )}
            />
          </Field>
        </Box>
      </Flex>
      <Text mb={3} color={'lightText'}>
        *Phone number will be anonymized for privacy and will not be seen by
        workers.
      </Text>
      <Controller
        render={({ field }) => (
          <Checkbox {...field} value={field.value ?? true}>
            Invite this person to my company's WorkWhile account
          </Checkbox>
        )}
        name={`supervisors.${index}.sendInvite` as const}
      />
      {sendInvite ? (
        <Flex flexWrap={'wrap'} mt={3}>
          <Box width={[1, 1 / 2]} pr={[0, 2]}>
            <Field label={'Email'} error={errors?.email?.message?.toString()}>
              <Input
                {...register(`supervisors.${index}.email` as const)}
                placeholder={'Enter Email'}
              />
            </Field>
          </Box>
          <Box width={[1, 1 / 2]} pr={[0, 2]}>
            <Field label={'Role'} error={errors?.roleId?.message?.toString()}>
              <Controller
                name={`supervisors.${index}.roleId` as const}
                render={({ field }) => (
                  <Select
                    options={roleOptions}
                    value={roleOptions.find(
                      (option) => option.value === field.value
                    )}
                    placeholder={'Select a Role'}
                    onChange={(newValue) => {
                      const data = newValue as Selectable<unknown>
                      field.onChange(data.value)
                    }}
                  />
                )}
              />
            </Field>
          </Box>
        </Flex>
      ) : null}
    </Box>
  )
}
